import {Injectable} from '@angular/core';
import {AuthGuard} from '../common/auth-guard.service';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {Geocoding} from "../model/geocoding";
import {HttpUtils} from "../shared/http.utils";

@Injectable({
  providedIn: 'root'
})
export class GeocodingService {

  private readonly geocodingURL: string;


  constructor(private authGuard: AuthGuard, private http: HttpClient) {
    this.geocodingURL = '/api/geocoding';
  }

  public getMachineGeocodings(language: string): Observable<{ geocodings: Geocoding[] }> {
    const url: string = `${this.geocodingURL}/all`;
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["language"] = language;
    options.params["fromMachine"] = true;
    options.params["fromOrganization"] = false;
    return this.http.get<{ geocodings: Geocoding[] }>(url, options);
  }

  public getOrganizationGeocodings(language: string): Observable<{ geocodings: Geocoding[] }> {
    const url: string = `${this.geocodingURL}/all`;
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["language"] = language;
    options.params["fromMachine"] = false;
    options.params["fromOrganization"] = true;
    return this.http.get<{ geocodings: Geocoding[] }>(url, options);
  }

  public getGeocoding(longitude: number, latitude: number): Observable<{ geocoding: Geocoding }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    options.params["longitude"] = longitude;
    options.params["latitude"] = latitude;
    return this.http.get<{ geocoding: Geocoding }>(this.geocodingURL, options);
  }

  public getGeocodingData(geocodings: Geocoding[], language: string): GeocodingDataResponse {
    let countryStates: Map<string, Set<string>> = new Map<string, Set<string>>();
    let stateLocalities: Map<string, Set<string>> = new Map<string, Set<string>>();
    let countriesSet: Set<any> = new Set<any>();
    let statesSet: Set<any> = new Set<any>();
    let localitiesSet: Set<any> = new Set<any>();
    geocodings.forEach(geocoding => {
      countriesSet.add(geocoding.country[language]);
      statesSet.add(geocoding.state[language]);
      localitiesSet.add(geocoding.locality[language]);
      if (countryStates.has(geocoding.country[language])) {
        countryStates.get(geocoding.country[language]).add(geocoding.state[language]);
      } else {
        countryStates.set(geocoding.country[language], new Set([geocoding.state[language]]));
      }
      if (stateLocalities.has(geocoding.state[language])) {
        stateLocalities.get(geocoding.state[language]).add(geocoding.locality[language]);
      } else {
        stateLocalities.set(geocoding.state[language], new Set([geocoding.locality[language]]));
      }
    });

    let countries: string[] = Array.from(countriesSet);
    let states: string[] = Array.from(statesSet);
    let localities: string[] = Array.from(localitiesSet);
    countries.sort((a, b) => a.localeCompare(b));
    states.sort((a, b) => a.localeCompare(b));
    localities.sort((a, b) => a.localeCompare(b));

    let geocodingDataResponse = new GeocodingDataResponse();
    geocodingDataResponse.countries = countries.map(country => {
      return {name: country}
    });
    geocodingDataResponse.states = states.map(state => {
      return {name: state}
    });
    geocodingDataResponse.localities = localities.map(locality => {
      return {name: locality}
    });
    geocodingDataResponse.countryStates = countryStates;
    geocodingDataResponse.stateLocalities = stateLocalities;
    return geocodingDataResponse;
  }
}

export class GeocodingDataResponse {
  countries: { name: string }[];
  states: { name: string }[];
  localities: { name: string }[];
  countryStates: Map<string, Set<string>>;
  stateLocalities: Map<string, Set<string>>;
}
